import {
    getGrossToNetDate,
    quarterEndBeforeInception,
    isOlderThanLipper
} from '../../utils/fund-info';
import { comparisonColors } from '../../utils/colors';
import { displayShortDate } from '../../utils/Format/Format';

export const appendSymbol = (label, val) => (val >= 40 ? `${label} †` : label);
export const showUnusualPerformanceFooter = (values) => {
    if (!values.length) {
        return false;
    }
    const comparisons = values.map((val) => val >= 40);
    return comparisons.includes(true);
};

export const getDisclaimerNumAvgAnnChart = (iClass, isMoneyMarket, bond) => {
    if (!iClass && (!isMoneyMarket && !bond)) {
        return '²';
    }
    if (iClass && !isMoneyMarket && !bond) {
        return '³';
    }
    if (!iClass && bond) {
        return '³';
    }
    if (!iClass && isMoneyMarket) {
        return '⁴';
    }
    if (iClass && bond) {
        return '⁴';
    }
    if (iClass && isMoneyMarket) {
        return '⁵';
    }
    return '';
};

export const disclaimerNumbersAvgAnnChart = (labelText, ...rest) =>
    `${labelText}${getDisclaimerNumAvgAnnChart(...rest)}`;

export const displayHeaders = (
    ticker,
    isMoneyMarket,
    name,
    benchmarkName,
    primaryBenchmarkName,
    benchmarkCode,
    primaryBenchmarkCode,
    primaryBenchMarkPerformance
) => {
    if (
        (ticker !== 'RPSIX' && isMoneyMarket) ||
        benchmarkCode === primaryBenchmarkCode ||
        (primaryBenchMarkPerformance === null ||
            (primaryBenchMarkPerformance &&
                !Object.keys(primaryBenchMarkPerformance).length))
    ) {
        /* Commented original return code and updated it to return only primaryBaenchMarkName as per US177723 - Remove Secondary Benchmark
        return [name, benchmarkName];
        */
        return [name, primaryBenchmarkName];
    }

    if (ticker === 'RPSIX') {
        return [name, primaryBenchmarkName];
    }
    /* Commented original return code and updated it to return only primaryBaenchMarkName as per US177723 - Remove Secondary Benchmark
    return [name, benchmarkName, primaryBenchmarkName];
    */
    return [name, primaryBenchmarkName];
};

export const displayData = (
    ticker,
    isMoneyMarket,
    yrReturn,
    yrBench,
    year,
    benchmarkCode,
    primaryBenchmarkCode,
    primaryBenchMarkPerformance
) => {
    if (
        (ticker !== 'RPSIX' && isMoneyMarket) ||
        benchmarkCode === primaryBenchmarkCode ||
        (primaryBenchMarkPerformance === null ||
            (primaryBenchMarkPerformance &&
                !Object.keys(primaryBenchMarkPerformance).length))
    ) {
        if (!yrReturn) {
            return ['', ''];
        }
        /* Commented original return code and updated it to return only primaryBenchMarkName as per US177723 - Remove Secondary Benchmark
        return [yrReturn / 100, year / 100];
        */

        return [yrReturn / 100, yrBench / 100];
    }

    if (ticker === 'RPSIX') {
        if (!yrReturn) {
            return ['', ''];
        }
        return [yrReturn / 100, yrBench / 100];
    }

    if (!yrReturn) {
        return ['', '', ''];
    }
    /* Commented original return code and updated it to return only primaryBenchMarkName as per US177723 - Remove Secondary Benchmark
    return [yrReturn / 100, year / 100, yrBench / 100];
    */
    return [yrReturn / 100, yrBench / 100];
};

export const displayLegendData = (
    ticker,
    isMoneyMarket,
    name,
    benchmarkName,
    primaryBenchmarkName,
    benchmarkCode,
    primaryBenchmarkCode,
    primaryBenchMarkPerformance,
    grossToNetDate
) => {
    if (
        (ticker !== 'RPSIX' && isMoneyMarket) ||
        benchmarkCode === primaryBenchmarkCode ||
        (primaryBenchMarkPerformance === null ||
            (primaryBenchMarkPerformance &&
                !Object.keys(primaryBenchMarkPerformance).length))
    ) {
        /*  Commented original return code and updated it to return only primaryBenchMarkName as per US177723 - Remove Secondary Benchmark
        if (benchmarkName) {
            return [
                { label: name, color: comparisonColors[0] },
                { label: benchmarkName, color: comparisonColors[1] }
            ];
        }
        */
        if (benchmarkName) {
            return [
                { label: name, color: comparisonColors[0] },
                { label: primaryBenchmarkName, color: comparisonColors[1] }
            ];
        }
        return [{ label: name, color: comparisonColors[0] }];
    }
    if (benchmarkName) {
        if (ticker === 'RPSIX') {
            return [
                { label: name, color: comparisonColors[0] },
                {
                    label: `${primaryBenchmarkName} ${
                        grossToNetDate ? 'ᵛ' : ''
                    }`,
                    color: comparisonColors[1]
                }
            ];
        }
        /*  Commented original return code and updated it to return only primaryBenchMarkName as per US177723 - Remove Secondary Benchmark
        return [
            { label: `${benchmarkName}`, color: comparisonColors[1] },
            {
                label: `${primaryBenchmarkName} ${grossToNetDate ? 'ᵛ' : ''}`,
                color: comparisonColors[2]
            }
        ]; 
        */
        return [
            { label: name, color: comparisonColors[0] },
            {
                label: `${primaryBenchmarkName} ${grossToNetDate ? 'ᵛ' : ''}`,
                color: comparisonColors[1]
            }
        ];
    }
    return [
        { label: name, color: comparisonColors[0] },
        {
            label: `${primaryBenchmarkName} ${grossToNetDate ? 'ᵛ' : ''}`,
            color: comparisonColors[2]
        }
    ];
};

export const averageAnnualTotalReturnsCardUtil = (
    ticker,
    isMoneyMarket,
    iClass,
    bond
) => (gqlResult) => {
    if (!gqlResult) return null;

    const {
        mutualFund: {
            name,
            inceptionDate,
            tackedReturnFundInceptionDate,
            benchmarkPerformanceDetail: {
                benchmarkName,
                primaryBenchmarkName,
                benchmarkCode,
                primaryBenchmarkCode,
                primaryBenchMarkPerformance,
                benchMarkPerformance
            },
            standardPerformanceReturns
        }
    } = gqlResult;

    // Allows for handling of null values
    const {
        oneYearReturn: oneYrBench,
        threeYearReturn: threeYrBench,
        fiveYearReturn: fiveYrBench,
        tenYearReturn: tenYrBench,
        sinceInceptionReturn: sinceInceptionBench
    } =
        primaryBenchMarkPerformance || {};

    const { quarterlyAnnualReturns } = standardPerformanceReturns || {};

    const {
        fundPerformance: {
            asOfDate,
            oneYearReturn,
            threeYearReturn,
            fiveYearReturn,
            tenYearReturn,
            sinceInceptionReturn,
            oneYearReturnTacked,
            threeYearReturnTacked,
            fiveYearReturnTacked,
            tenYearReturnTacked,
            sinceInceptionReturnTacked
        }
    } = quarterlyAnnualReturns || { fundPerformance: {} };

    const {
        oneYearReturn: benchOneYear,
        threeYearReturn: benchThreeYear,
        fiveYearReturn: benchFiveYear,
        tenYearReturn: benchTenYear,
        sinceInceptionReturn: benchSinceInception
    } =
        benchMarkPerformance || {};

    const grossToNetDate = getGrossToNetDate(ticker);

    const title = 'Average Annual Total Returns';
    const SinceInceptionDate = displayShortDate(inceptionDate);
    const SinceInceptionDateInvestorClass = displayShortDate(
        tackedReturnFundInceptionDate
    );
    const sinceInceptionReturnValue = quarterEndBeforeInception(inceptionDate)
        ? null
        : sinceInceptionReturn;

    const quarterlyAsOfDate = displayShortDate(asOfDate);
    const legendData = displayLegendData(
        ticker,
        isMoneyMarket,
        name,
        benchmarkName,
        primaryBenchmarkName,
        benchmarkCode,
        primaryBenchmarkCode,
        primaryBenchMarkPerformance,
        // @note:
        // eek this is not a great way to do this
        // we desperately need some type of disclaimer service
        // where we can specify disclaimers for a card.
        // with a given launch date...
        // and given typographic styles
        grossToNetDate
    );
    const returnData = [
        {
            label: appendSymbol('1 Year', oneYearReturn),
            headers: displayHeaders(
                ticker,
                isMoneyMarket,
                name,
                benchmarkName,
                primaryBenchmarkName,
                benchmarkCode,
                primaryBenchmarkCode,
                primaryBenchMarkPerformance
            ),
            data: displayData(
                ticker,
                isMoneyMarket,
                oneYearReturn,
                oneYrBench,
                benchOneYear,
                benchmarkCode,
                primaryBenchmarkCode,
                primaryBenchMarkPerformance
            )
        },
        {
            label: appendSymbol('3 Year', threeYearReturn),
            headers: displayHeaders(
                ticker,
                isMoneyMarket,
                name,
                benchmarkName,
                primaryBenchmarkName,
                benchmarkCode,
                primaryBenchmarkCode,
                primaryBenchMarkPerformance
            ),
            data: displayData(
                ticker,
                isMoneyMarket,
                threeYearReturn,
                threeYrBench,
                benchThreeYear,
                benchmarkCode,
                primaryBenchmarkCode,
                primaryBenchMarkPerformance
            )
        },
        {
            label: '5 Year',
            headers: displayHeaders(
                ticker,
                isMoneyMarket,
                name,
                benchmarkName,
                primaryBenchmarkName,
                benchmarkCode,
                primaryBenchmarkCode,
                primaryBenchMarkPerformance
            ),
            data: displayData(
                ticker,
                isMoneyMarket,
                fiveYearReturn,
                fiveYrBench,
                benchFiveYear,
                benchmarkCode,
                primaryBenchmarkCode,
                primaryBenchMarkPerformance
            )
        },
        {
            label: '10 Year',
            headers: displayHeaders(
                ticker,
                isMoneyMarket,
                name,
                benchmarkName,
                primaryBenchmarkName,
                benchmarkCode,
                primaryBenchmarkCode,
                primaryBenchMarkPerformance
            ),
            data: displayData(
                ticker,
                isMoneyMarket,
                tenYearReturn,
                tenYrBench,
                benchTenYear,
                benchmarkCode,
                primaryBenchmarkCode,
                primaryBenchMarkPerformance
            )
        },
        {
            label: `${disclaimerNumbersAvgAnnChart(
                'Since Inception',
                iClass,
                isMoneyMarket,
                bond
            )}${isOlderThanLipper(ticker) ? ' *' : ''}`,
            headers: displayHeaders(
                ticker,
                isMoneyMarket,
                name,
                benchmarkName,
                primaryBenchmarkName,
                benchmarkCode,
                primaryBenchmarkCode,
                primaryBenchMarkPerformance
            ),
            data: displayData(
                ticker,
                isMoneyMarket,
                sinceInceptionReturnValue,
                sinceInceptionBench,
                benchSinceInception,
                benchmarkCode,
                primaryBenchmarkCode,
                primaryBenchMarkPerformance
            )
        }
    ];

    const inceptionReturnMessage =
        (sinceInceptionReturn && !benchSinceInception) ||
        (sinceInceptionReturn && !sinceInceptionBench);

    let isPrimaryBenchMark = false;

    const noData = oneYearReturn === null;

    if (
        !isMoneyMarket &&
        primaryBenchMarkPerformance !== null &&
        Object.keys(primaryBenchMarkPerformance).length &&
        benchmarkCode === primaryBenchmarkCode
    ) {
        isPrimaryBenchMark = true;
    }

    return {
        data: returnData,
        title,
        noData,
        legendData,
        hasTacked: [
            oneYearReturnTacked,
            threeYearReturnTacked,
            fiveYearReturnTacked,
            tenYearReturnTacked,
            sinceInceptionReturnTacked
        ].includes(true),
        SinceInceptionDate,
        SinceInceptionDateInvestorClass,
        quarterlyAsOfDate,
        grossToNetDate,
        inceptionReturnMessage,
        isPrimaryBenchMark,
        showUnusualPerformanceFooter: showUnusualPerformanceFooter([
            oneYearReturn,
            threeYearReturn
        ])
    };
};

export const fundsWithBenchmarkFootnote = [
    'PRSGX',
    'PRAFX',
    'PRIKX',
    'TRRFX',
    'TRRAX',
    'TRRGX',
    'TRRBX',
    'TRRHX',
    'TRRCX',
    'TRRJX',
    'TRRDX',
    'TRRKX',
    'TRRMX',
    'TRRNX',
    'TRRLX',
    'TRRIX',
    'TRPTX',
    'TRPFX',
    'TRPAX',
    'TRFGX',
    'TRBRX',
    'TRPHX',
    'TRPCX',
    'TRPJX',
    'TRPDX',
    'TRPKX',
    'TRPMX',
    'TRPNX',
    'TRPLX',
    'TRLAX',
    'TRARX',
    'TFRRX',
    'TRROX',
    'TORFX',
    'TRRTX',
    'TTRTX',
    'TRRUX',
    'TTURX',
    'TRRVX',
    'TRVVX',
    'TRRWX',
    'TWRRX',
    'RPGRX',
    'TPGPX',
    'TRHRX',
    'TRXRX',
    'RPTFX',
    'TRFWX',
    'TRFOX',
    'TOORX',
    'TRFFX',
    'TRPPX',
    'TRTFX',
    'TTOIX'
];
